import { v4 as uuidv4 } from "uuid";

/**
 * Rounds a number to the specified decimal places using arithmetic rounding.
 * Handles floating-point precision edge cases (e.g. 1.005 → 1.01).
 */
export function roundTo(value, decimals = 2) {
  return Number(Math.round(Number(value + "e" + decimals)) + "e-" + decimals);
}

/**
 * Truncates a number to specified precision without rounding.
 * Returns a string with exactly `precision` decimal places.
 * Uses epsilon to avoid floating-point floor errors (e.g. 1.1 * 100 = 109.999...).
 */
export function truncateToPrecision(number, precision = 3) {
  const epsilon = 1e-10;
  const factor = Math.pow(10, precision);
  return (Math.trunc(number * factor + epsilon) / factor).toFixed(precision);
}

/**
 * Determines the effective tax-included flag from saleTypeData and tillData.
 * This logic is duplicated ~15 times across the codebase.
 */
export function getTaxIncludeFlag(saleTypeData, tillData) {
  if (saleTypeData?.customer?.istaxincluded === "Y") return "Y";
  return tillData?.tillAccess?.csBunit?.isTaxIncluded || "Y";
}

/**
 * Creates an empty cart object with fresh UUIDs.
 * Matches the initial state shape from Core/index.js.
 */
export function initializeCart(overrides = {}) {
  return {
    items: [],
    couponInput: [],
    advancePayment: 0,
    total: 0,
    tax: 0,
    discount: 0,
    paid: 0,
    change: 0,
    totalQty: 0,
    roundOff: 0,
    payments: [],
    redemptionPoints: 0,
    accumulationPoints: 0,
    creditAmount: 0,
    docType: "Invoice",
    sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
    referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
    giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
    couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
    customer: null,
    salesRepId: null,
    cardPaymentData: {},
    documentno: "",
    userName: "",
    tillName: "",
    subTotal: 0,
    itemsSummery: {},
    amountInWords: "",
    groupedItemsByTaxRate: {},
    serviceCharge: 0,
    tip: 0,
    deiveryCharge: 0,
    packingCharge: 0,
    others: 0,
    refundAmount: 0,
    ...overrides,
  };
}

/**
 * Calculates tax amount for a given net total and tax rate.
 * @param {number} nettotal - Net total amount
 * @param {number} taxRate - Tax rate percentage
 * @param {string} taxIncludeFlag - "Y" if tax is included in price
 * @returns {number} Tax amount
 */
export function calculateTax(nettotal, taxRate, taxIncludeFlag = "Y") {
  if (!taxRate || taxRate === 0) return 0;
  if (taxIncludeFlag === "Y") {
    return nettotal - nettotal / (1 + taxRate / 100);
  }
  return (nettotal * taxRate) / 100;
}

/**
 * Gets the price precision from tillData.
 */
export function getPrecision(tillData) {
  return tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
}

/**
 * Gets the rounding method from tillData currency config.
 * RU = Round Up, RD = Round Down, RN = Round Nearest, NR = No Rounding
 */
export function getRoundingMethod(tillData) {
  return tillData?.tillAccess?.csBunit?.currencies?.[0]?.roundingMethod || "NR";
}

/**
 * Applies currency-based rounding to the gross total.
 * Returns the rounded total and the round-off difference.
 * roundOffAmount sign: positive = customer pays less (rounded down),
 *                      negative = customer pays more (rounded up).
 *
 * @param {number} grossTotal - The unrounded gross total
 * @param {string} method - RU (ceil), RD (floor), RN (round), NR (none)
 * @returns {{ roundedTotal: number, roundOffAmount: number }}
 */
export function applyCurrencyRounding(grossTotal, method = "NR") {
  if (!method || method === "NR") {
    return { roundedTotal: grossTotal, roundOffAmount: 0 };
  }

  let roundedTotal;
  switch (method) {
    case "RU":
      roundedTotal = Math.ceil(grossTotal);
      break;
    case "RD":
      roundedTotal = Math.floor(grossTotal);
      break;
    case "RN":
      roundedTotal = Math.round(grossTotal);
      break;
    default:
      return { roundedTotal: grossTotal, roundOffAmount: 0 };
  }

  const roundOffAmount = grossTotal - roundedTotal;
  return { roundedTotal, roundOffAmount };
}
